import api from './api';

export interface OverrideApprovalItem {
  pronumber: number;
  legnum: number;
  /** Stable legsall identifier. Nullable for new legs whose post-save legrecno
   *  couldn't be resolved at POST time. Used for the (driver, legrecno) dedupe
   *  check that suppresses the prompt on subsequent saves. */
  legrecno?: number | null;
  driver: string;
  reason: string;
  driver_not_qualified_text?: string | null;
  /** Snapshot of the order-level qualification inputs at the moment the
   *  override was granted. Sourced from the post-save order state so the
   *  audit row records the values the driver was actually checked against. */
  lbs?: number | null;
  hazmat?: string | null;
  tank?: string | null;
  daycab?: string | null;
  otr?: string | null;
  size?: string | null;
  equiptype?: string | null;
  chassistype?: string | null;
}

export interface OverrideApprovalRecord extends OverrideApprovalItem {
  id: number;
  username: string;
  /** ISO timestamp produced by the server. */
  created_at: string;
}

class DispatchOverrideApprovalService {
  private baseUrl = '/dispatch-override-approvals';

  /**
   * Record one or more dispatch override approvals from the pro details
   * save flow. Each item is one row in `dispatch_override_approvals`. When
   * a single user prompt covered multiple (driver, leg) failures, send N
   * items sharing the same `reason` text.
   */
  async recordApprovals(items: OverrideApprovalItem[]): Promise<{ success: boolean; count: number }> {
    if (items.length === 0) return { success: true, count: 0 };
    const response = await api.post(`${this.baseUrl}/batch`, { items });
    return response.data;
  }

  /**
   * List every recorded override approval for a PRO. Pro details uses this to:
   *   - dedupe the save-time prompt (skip already-approved (driver, legrecno))
   *   - render the inline override indicator on matching leg rows
   * Returned newest-first by created_at.
   */
  async getByPro(pronumber: number, signal?: AbortSignal): Promise<OverrideApprovalRecord[]> {
    const response = await api.get<OverrideApprovalRecord[]>(
      `${this.baseUrl}/by-pro/${pronumber}`,
      { signal },
    );
    return response.data;
  }
}

const dispatchOverrideApprovalService = new DispatchOverrideApprovalService();
export default dispatchOverrideApprovalService;
